import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResizeDetector } from 'react-resize-detector';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import {
  ICON_NOTICE_RED,
  ICON_PLUS_BLACK,
  ICON_PLUS_WHITE,
  ICON_CHECK_DISABLE,
  ICON_CHECK_ABLE,
} from 'assets/icons';
import ProductItem from './components/ProductItem';
import { useQueryGetProductList } from './graphQL/hooks/useQueryGetProductList';
import { PRODUCT_REGIST_PATH } from 'constants/path';
import ProductFilterModal from './components/ProductFilterModal';
import Column from 'components/common/Column';
import ProductRegistModal from './components/ProductRegistModal';
import { ProductState } from 'types/api';
import ProductItemModal from './components/ProductItemModal';
import { useProductFilterHooks } from './hooks/useProductFilterHooks';
import Loading from 'components/Loading/Loading';
import {
  formatProductStatus,
  formatSalesTermType,
} from 'utils/hackle/parameterFormatting';
import HackleManager from 'utils/hackle';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import BasicButton from 'components/Button/BasicButton';
import CommonImage from 'components/common/CommonImage';
import CommonFilter from 'components/common/CommonFilter';
import Divider from 'components/Divider';
import { HEADER_HEIGHT } from 'constants/style';

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  width: 100%;
  max-width: 1200px;
  min-height: 100vh;

  border-left: 1px solid #dbdcdf;
  border-right: 1px solid #dbdcdf;

  background-color: #fff;

  &:first-child {
    gap: 0px;
  }

  @media (max-width: 768px) {
    border-left: none;
    border-right: none;
  }
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 12px;

  border-radius: 12px;
  background: #f4f4f5;

  color: #414b56;

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.6px;
`;

const ListContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-wrap: wrap;

  gap: 40px;
  width: 100%;
  min-height: 100vh;
`;

const RejectContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  width: 100%;

  border-radius: 12px;
  border: 1px solid var(--gray-gray-03, #eaebec);
  background: var(--gray-gray-01, #f7f7f8);
`;

const ProductListPage = () => {
  const {
    isLoading,
    myInfo,
    productList,
    productfilterInput,
    isAcceptFilter,
    selectProduct,
    isOpenSelectProductModal,
    setProductFilterInput,
    setIsOpenSelectProductModal,
    handleSetSelectProduct,
    handleLoadMoreProductList,
    setIsAcceptFilter,
  } = useQueryGetProductList();

  const {
    searchValue,
    productStatus,
    termWithType,
    termType,
    setTermType,
    handlerSetSearchValue,
    handleSetProductStatus,
    handleSetTermWithType,
    handleResetFilterInput,
  } = useProductFilterHooks();

  const navigate = useNavigate();

  const { width, ref } = useResizeDetector();

  const [isOpenFilterModal, setIsOpenFilterModal] = useState<boolean>(false);
  const [isOpenProductRegistBottomSheet, setIsOpenProductRegistBottomSheet] =
    useState<boolean>(false);

  const handleSetSaleProduct = () => {
    if (productfilterInput.statusIn?.includes(ProductState.SALE)) {
      handleSetProductStatus('all');
      setProductFilterInput({
        ...productfilterInput,
        statusIn: null,
      });

      return;
    }

    handleSetProductStatus('select', ProductState.SALE);
    setProductFilterInput({
      ...productfilterInput,
      statusIn: [ProductState.SALE],
    });
  };

  const isMobile = width ? width < 768 : window.innerWidth < 768;
  const isTablet = width && width > 768 && width < 1024;

  useEffect(() => {
    if (productList) {
      HackleManager.logProductView(
        productList.product.products.totalCount,
        searchValue,
        formatSalesTermType(termType),
        productStatus.type === 'all'
          ? '전체'
          : formatProductStatus(productfilterInput.statusIn),
      );

      HackleManager.logProductFilterComplete(
        productList.product.products.totalCount,
        searchValue,
        formatSalesTermType(termType),
        productStatus.type === 'all'
          ? '전체'
          : formatProductStatus(productfilterInput.statusIn),
      );
    }
  }, [productList]);

  const renderRejectNotice = (): JSX.Element => {
    const isRejectProduct =
      myInfo.me.host.productInfo &&
      myInfo.me.host.productInfo.counts.find(
        item => item.status === ProductState.REJECTED,
      );

    if (isRejectProduct) {
      return (
        <RejectContainer>
          <img src={ICON_NOTICE_RED} alt="icon notice red" />
          <Column gap="16px" align="flex-start">
            <Column gap="4px" align="flex-start">
              <Text color="#1B1C1E" size="14px" weight={600}>
                검수되지 않은 프립이 있어요
              </Text>
              <Text color="#1B1C1E" size="13px" weight={400}>
                반려 내용을 확인하여 수정 후 재검수를 요청해 주세요
              </Text>
            </Column>
            <Text
              color="#1b1c1e"
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                handleSetProductStatus('select', ProductState.REJECTED);
                setIsAcceptFilter(true);
                setProductFilterInput({
                  ...productfilterInput,
                  statusIn: [ProductState.REJECTED],
                });
              }}
            >
              확인하기
            </Text>
          </Column>
        </RejectContainer>
      );
    }

    return <></>;
  };

  const renderProductList = (): JSX.Element => {
    if (productList) {
      return (
        <React.Fragment>
          {/* myInfo.me.host.productInfo?.counts.length === 0 || */}
          {isEmpty(productList.product.products.edges) ? (
            <Column gap="24px" style={{ minHeight: '65vh' }}>
              <EmptySettlement
                textContent={
                  <Column gap="4px">
                    <Text
                      color="#46474C"
                      size="20px"
                      weight={isMobile ? 600 : 500}
                      style={{ letterSpacing: '-0.12px' }}
                    >
                      등록된 상품이 없어요.
                    </Text>
                    <Text
                      color="#46474C"
                      size="16px"
                      weight={isMobile ? 400 : 300}
                      style={{ letterSpacing: '-0.064px' }}
                    >
                      호스팅할 상품을 등록해 보세요!
                    </Text>
                  </Column>
                }
                style={{ minHeight: 'auto' }}
              />
              <BasicButton
                content={
                  <Row gap="2px">
                    <CommonImage src={ICON_PLUS_WHITE} />
                    <Text
                      color="#fff"
                      size="16px"
                      weight={isMobile ? 600 : 500}
                    >
                      상품 등록하기{' '}
                    </Text>
                  </Row>
                }
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#0071E9',
                }}
                onClick={() => {
                  HackleManager.logProductCreateClick();
                  navigate(`${PRODUCT_REGIST_PATH}/draft`);
                }}
              />
            </Column>
          ) : (
            <ListContainer>
              {productList.product.products.edges.map((item, idx) => {
                return (
                  <ProductItem
                    key={idx}
                    item={item}
                    width={width}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    handleSetSelectProduct={handleSetSelectProduct}
                  />
                );
              })}
              <InView
                onChange={inView => {
                  if (
                    inView &&
                    productList.product.products.pageInfo.hasNextPage
                  ) {
                    handleLoadMoreProductList();
                  }
                }}
              />
            </ListContainer>
          )}
        </React.Fragment>
      );
    }

    return <></>;
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container ref={ref} $isMobile={isMobile}>
      <Column>
        <Row
          justifyContent="space-between"
          style={{
            position: 'sticky',
            top: isMobile ? 0 : HEADER_HEIGHT,
            padding: isMobile ? '24px 20px' : '32px 40px ',
            width: '100%',
            background: '#fff',
            zIndex: 10,
          }}
        >
          <Text color="#1b1c1e" size="24px" style={{ whiteSpace: 'nowrap' }}>
            상품
          </Text>
          <Row
            gap={isMobile ? '8px' : '24px'}
            justifyContent="flex-end"
            style={{ flexWrap: 'wrap' }}
          >
            <Row gap={isMobile ? '8px' : '16px'}>
              <Row
                gap="4px"
                style={{ cursor: 'pointer' }}
                onClick={handleSetSaleProduct}
              >
                <CommonImage
                  src={
                    productStatus.type !== 'all' &&
                    productfilterInput.statusIn?.includes(ProductState.SALE)
                      ? ICON_CHECK_ABLE
                      : ICON_CHECK_DISABLE
                  }
                />
                <Text
                  color={
                    productStatus.type !== 'all' &&
                    productfilterInput.statusIn?.includes(ProductState.SALE)
                      ? '#414B56'
                      : '#c2c4c8'
                  }
                  size="16px"
                >
                  {isMobile ? '판매 중만' : '판매중 상품만 보기'}
                </Text>
              </Row>
              <Divider
                style={{
                  width: '1px',
                  height: '16px',
                  background: '#DBDCDF',
                }}
              />
              <CommonFilter
                isAcceptFilter={isAcceptFilter}
                onClick={() => {
                  setIsOpenFilterModal(true);
                }}
                style={{ padding: '0px' }}
              />
            </Row>
            <SelectButton
              onClick={() => {
                if (
                  myInfo.me.host.productInfo?.counts.length === 0 ||
                  isEmpty(productList.product.products.edges)
                ) {
                  navigate(`${PRODUCT_REGIST_PATH}/draft`);
                  return;
                }

                setIsOpenProductRegistBottomSheet(true);
              }}
              style={{ height: '40px' }}
            >
              <CommonImage src={ICON_PLUS_BLACK} />

              <Text color="#414b56" size="14px" weight={isMobile ? 600 : 500}>
                상품 등록하기
              </Text>
            </SelectButton>
          </Row>
        </Row>
        {/* <Column gap="24px" align="flex-end"> */}
        <Column
          gap="40px"
          style={{ padding: isMobile ? '0 20px 95px' : '0 40px 40px' }}
        >
          {renderRejectNotice()}
          {renderProductList()}
        </Column>
        {/* </Column> */}

        {isOpenFilterModal && (
          <ProductFilterModal
            isMobile={isMobile}
            productfilterInput={productfilterInput}
            searchValue={searchValue}
            productStatus={productStatus}
            termWithType={termWithType}
            termType={termType}
            setTermType={setTermType}
            handlerSetSearchValue={handlerSetSearchValue}
            handleSetProductStatus={handleSetProductStatus}
            handleResetFilterInput={handleResetFilterInput}
            handleSetTermWithType={handleSetTermWithType}
            setProductFilterInput={setProductFilterInput}
            setIsAcceptFilter={setIsAcceptFilter}
            onClose={() => setIsOpenFilterModal(false)}
          />
        )}
        {isOpenProductRegistBottomSheet && (
          <ProductRegistModal
            myInfo={myInfo}
            isMobile={isMobile}
            onClose={() => setIsOpenProductRegistBottomSheet(false)}
          />
        )}
        {isOpenSelectProductModal && (
          <ProductItemModal
            isMobile={isMobile}
            selectProduct={selectProduct}
            onClose={() => setIsOpenSelectProductModal(false)}
          />
        )}
      </Column>
    </Container>
  );
};

export default ProductListPage;
